import {useAuth} from "react-oidc-context";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    IconButton,
    Paper,
    Typography
} from "@mui/material";
import {ArrowBack} from "@mui/icons-material";
import {Company} from "../entities/company";
import {TokenRequest} from "../entities/token-request";
import {useSnackbar} from "notistack";
import {TokensWithInvitation} from "../components/TokensWithInvitation";

export const TokenRequestPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar()
    const [company, setCompany] = useState<Company>()
    const [tokenRequest, setTokenRequest] = useState<TokenRequest>()
    const {companyId, tokenRequestId} = useParams();

    useEffect(() => {
        if (auth.isAuthenticated && (!company)) {
            const token = auth.user?.access_token;
            fetch(`/api/admin/companies/${companyId}`, {headers: {"Authorization": `Bearer ${token}`}})
                .then(res => res.json())
                .then(data => {
                    setCompany(data)
                })
                .then(() => {
                    return fetch(`/api/admin/companies/${companyId}/token_requests/${tokenRequestId}`, {headers: {"Authorization": `Bearer ${token}`}})
                })
                .then(res => res.json())
                .then(data => {
                    setTokenRequest(data)
                })
                .catch(error => {
                    console.log(error)
                    enqueueSnackbar(`An error has occurred while retrieving company & token information`, {
                        variant: 'error',
                        autoHideDuration: 10_000
                    });
                    navigate(-1)
                });

            console.log("no info in state")
        }
    }, [auth.isAuthenticated, tokenRequestId, companyId, company, tokenRequest, auth.user?.access_token, enqueueSnackbar, navigate]);


    const cancelTokenRequest = () => {
        const token = auth.user?.access_token;
        fetch(`/api/admin/companies/${companyId}/token_requests/${tokenRequestId}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
            },
            method: "DELETE"
        }).then(() => {
            window.location.reload();
        }).catch(error => {
            console.log(error)
            enqueueSnackbar(`An error has occurred while cancelling the token`, {variant: 'error'});
            window.location.reload();
        });
    }

    const validateOrDenyTokenRequest = (targetStatus: string) => {
        const token = auth.user?.access_token;
        fetch(`/api/admin/companies/${companyId}/token_requests/${tokenRequestId}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            method: "PUT",
            body: JSON.stringify({status: targetStatus})
        }).then((res) => {
            if (res.ok) {
                window.location.reload();
            } else {
                throw Error()
            }
        }).catch(error => {
            console.log(error)
            enqueueSnackbar(`An error has occurred while validating the token`, {
                variant: 'error',
                autoHideDuration: 10_000
            });
        });
    }

    const mapHistory = () => {
        return tokenRequest?.history?.map(tokenRequestHistoryEntry =>
            <p>{new Date(tokenRequestHistoryEntry.lastModifiedAt).toLocaleString("fr-be")} - {tokenRequestHistoryEntry.lastModifiedBy} - {tokenRequestHistoryEntry.status} - {tokenRequestHistoryEntry.requestedQuantity}</p>)
    }

    const mapStatusToAvailableActions = () => {
        if (tokenRequest?.status === "DELIVERED") {
            return <Button onClick={() => cancelTokenRequest()} color="error" variant="outlined">Cancel</Button>
        } else if (tokenRequest?.status === "PENDING") {
            return <>
                <Button onClick={() => cancelTokenRequest()} color="error" variant="outlined">Cancel</Button>
                <Button onClick={() => validateOrDenyTokenRequest('DENIED')} color="error" variant="outlined">Deny</Button>
                <Button onClick={() => validateOrDenyTokenRequest('DELIVERED')} color="success" variant="outlined">Validate</Button>
            </>
        } else {
            return <></>
        }
    }


    const loadProof = () => {
        fetch(`/api/admin/companies/${companyId}/token_requests/${tokenRequestId}/proofs`, {headers: {"Authorization": `Bearer ${auth?.user?.access_token}`}})
            .then(res => {
                if (res.ok) {
                    return res.blob()
                }
                throw Error("Unable to read the file content")
            })
            .then(data => {
                const url = URL.createObjectURL(data)
                window.open(url)
            }).catch(error => {
            enqueueSnackbar(`An error has occurred while loading proof ${error}`, {variant: 'error'});
        })

    }

    return company && tokenRequest ? <Paper>
            <Card>
                <IconButton onClick={() => navigate(-1)}
                            size="small"><ArrowBack/>Back</IconButton>
                <CardHeader title={company.name}/>
                <CardContent>
                    <Typography variant="caption" color="primary">
                        Created at {new Date(company.createdAt).toLocaleString("fr-be")}
                    </Typography><Typography variant="caption"> by {company.createdBy}</Typography>
                    <Typography variant="caption" component="p">
                        Last update
                        on {new Date(company.lastModifiedAt).toLocaleString("fr-be")} by {company.lastModifiedBy}
                    </Typography>
                </CardContent>
            </Card>
            <Card>
                <CardHeader title={`Token Request N°${tokenRequest.id}`}/>
                <CardActions>
                    {mapStatusToAvailableActions()}
                </CardActions>
                <CardContent>
                    <Typography component="div">
                        <p>Requested
                            by {tokenRequest.requestingUser} on {new Date(tokenRequest.requestDate).toLocaleString("fr-be")}</p>
                        <p>Last
                            update: {new Date(tokenRequest.lastModifiedAt).toLocaleString("fr-be")} by {tokenRequest.lastModifiedBy}</p>
                        <p>Quantity requested: {tokenRequest.requestedQuantity}</p>
                        <p>Current status: {tokenRequest.status}</p>
                        <p>Proof of payment: {tokenRequest.proofOfPaymentName && <Button variant="outlined"
                                                                                         onClick={() => {
                                                                                             loadProof()
                                                                                         }}>{tokenRequest.proofOfPaymentName}</Button>}</p>
                    </Typography>
                </CardContent>
                <CardContent>
                    <Typography variant={"h6"}>History</Typography>
                    <Typography>
                        {(!tokenRequest.history || tokenRequest.history.length === 0) ?
                            <>No History to be displayed</>
                            : <>{mapHistory()}</>}
                    </Typography>
                </CardContent>
            </Card>
            <Card>
                <CardHeader title="Tokens & invitations"></CardHeader>
                <CardContent>
                    <Typography>
                        <TokensWithInvitation tokens={tokenRequest.tokens} showAdminInfos={true}/>
                    </Typography>
                </CardContent>
            </Card>
        </Paper>
        : <CircularProgress/>
}